import { Action } from "@/api-schema-enums";
import { useCasaviContactBlacklistCreateMutation } from "@/common/api/casavi/contact-blacklist/use-casavi-contact-blacklist-create.mutation";
import { useCasaviContactBlacklistUpdateMutation } from "@/common/api/casavi/contact-blacklist/use-casavi-contact-blacklist-update.mutation";
import { handleFormSubmission } from "@/common/helpers/form-submission";
import { Form } from "@/components/ui/form";
import { getCasaviFilterFormValues } from "@/modules/casavi/_helpers/casavi-filter-form-values";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import {
  CasaviContactBlacklist,
  CasaviContactBlacklistFormSchema,
  CasaviContactBlacklistFormSchemaType,
} from "../_helpers/validate-casavi-contact-blacklist";
import { ContactBlacklistFormFields } from "./contact-blacklist-form-fields";

interface ContactBlacklistFormProps {
  mode: Action.Create | Action.Update;
  uuid?: string;
  initialData?: CasaviContactBlacklist;
}

export function ContactBlacklistForm({ mode, uuid, initialData }: ContactBlacklistFormProps) {
  const { t } = useTranslation("casavi");
  const [_, navigate] = useLocation();
  const initialFormValues = useMemo(() => getCasaviFilterFormValues(initialData), [initialData]);

  const createMutation = useCasaviContactBlacklistCreateMutation();
  const updateMutation = useCasaviContactBlacklistUpdateMutation();

  const isPending = mode === Action.Create ? createMutation.isPending : updateMutation.isPending;

  const form = useForm<CasaviContactBlacklistFormSchemaType>({
    resolver: zodResolver(CasaviContactBlacklistFormSchema),
    mode: "onChange",
    defaultValues: initialFormValues,
  });

  useEffect(() => {
    if (initialData) {
      form.reset(initialFormValues, {
        keepDefaultValues: false,
      });
    }
  }, [form, initialData, initialFormValues]);

  const onSubmit = async (values: CasaviContactBlacklistFormSchemaType): Promise<void> => {
    const messages =
      mode === Action.Create
        ? {
            success: t("ContactBlackListSuccessCreate"),
            error: t("ContactBlackListErrorCreate"),
          }
        : {
            success: t("ContactBlackListSuccessUpdate"),
            error: t("ContactBlackListErrorUpdate"),
          };

    if (mode === Action.Create) {
      await handleFormSubmission(
        () => createMutation.mutateAsync(values),
        messages,
        () => navigate("~/cas/contact-blacklist"),
      );
    } else {
      if (!uuid) return;

      await handleFormSubmission(
        () =>
          updateMutation.mutateAsync({
            uuid,
            ...values,
          }),
        messages,
        () => navigate("~/cas/contact-blacklist"),
      );
    }
  };

  return (
    <Form {...form}>
      <ContactBlacklistFormFields
        form={form}
        onSubmit={onSubmit}
        isPending={isPending}
        mode={mode}
        currentUuid={uuid}
      />
    </Form>
  );
}
